import { GridColDef, getGridStringOperators } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { selectUserCognitoGroups } from "../Users/usersSlice";
import { CognitoUserGroups } from "../Users/userUtils";
import { SysAdminsUserInvitationsTable } from "./SysAdminsUserInvitationsTable";
import { ProgramAdminsUserInvitationsTable } from "./ProgramAdminsUserInvitationsTable";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";

const pageDetails: PageWrapperProps = {
  className: "UserInvitations",
  headerText: "User Invitations"
};

export const UserInvitations = ({
  disableVirtualization = false
}: {
  disableVirtualization?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useAppSelector(selectUserCognitoGroups);
  const isSysAdmin = permissions?.includes(CognitoUserGroups.SysAdmins);
  const isProgramAdmin = permissions?.includes(CognitoUserGroups.ProgramAdmins);
  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const { allow_bulk_invite } = flags;

  const getActiveMemberships = (row: any) =>
    (row?.invitee?.programMemberships?.items || []).filter(
      (m: any) => !m?.endDate
    );
  const joinUnique = (arr: (string | null | undefined)[]) => {
    const set = new Set(
      (arr || [])
        .map((v) => (typeof v === "string" ? v.trim() : v))
        .filter((v): v is string => !!v && v.length > 0)
    );
    return Array.from(set).join(", ");
  };

  const preColumns: GridColDef[] = [
    {
      headerName: t("Email"),
      field: "email",
      flex: 1
    },
    {
      headerName: t("First Name"),
      field: "inviteeFirstName",
      valueGetter: (params) => params.row.invitee.firstName,
      flex: 1,
      sortable: isSysAdmin ? false : true,
      filterable: isSysAdmin ? false : true
    },
    {
      headerName: t("Last Name"),
      field: "inviteeLastName",
      valueGetter: (params) => params.row.invitee.lastName,
      flex: 1,
      sortable: isSysAdmin ? false : true,
      filterable: isSysAdmin ? false : true
    },
    {
      headerName: t("Invitation Status"),
      field: "invitationStatus",
      flex: 1,
      sortable: isSysAdmin ? false : true
    },
    {
      headerName: t("Institution"),
      field: "institutionName",
      valueGetter: (params) => {
        const memberships = getActiveMemberships(params.row);
        return joinUnique(
          memberships.map(
            (m: any) => m?.program?.institution?.name as string | undefined
          )
        );
      },
      flex: 1,
      sortable: isSysAdmin ? false : true
    },
    {
      headerName: t("Program"),
      field: "programName",
      valueGetter: (params) => {
        const memberships = getActiveMemberships(params.row);
        return joinUnique(
          memberships.map((m: any) => m?.program?.name as string | undefined)
        );
      },
      flex: 1,
      sortable: isSysAdmin ? false : true
    },
    {
      headerName: t("Role"),
      field: "role",
      valueGetter: (params) => {
        const memberships = getActiveMemberships(params.row);
        return joinUnique(
          memberships.map((m: any) => m?.programRole as string | undefined)
        );
      },
      flex: 1,
      sortable: isSysAdmin ? false : true
    }
  ];

  const columns = preColumns.map((column) => {
    return {
      ...column,
      // only include the "contains" filter for now (help with server-side filtering)
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      )
    };
  });

  const sysAdminTable = (
    <SysAdminsUserInvitationsTable
      columns={columns}
      disableVirtualization={disableVirtualization}
    />
  );

  const programAdminTable = (
    <ProgramAdminsUserInvitationsTable
      columns={columns}
      disableVirtualization={disableVirtualization}
    />
  );

  const tableToDisplay = isSysAdmin
    ? sysAdminTable
    : isProgramAdmin
    ? programAdminTable
    : null;

  return (
    <PageWrapper {...pageDetails}>
      <div>
        <button onClick={() => navigate("/invitations/new")}>
          {t("New Invitation")}
        </button>
        {allow_bulk_invite && (
          <button onClick={() => navigate("/invitations/bulkupload")}>
            {t("Bulk Invite Users")}
          </button>
        )}
      </div>
      {tableToDisplay}
    </PageWrapper>
  );
};
